import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/kfmApi";
import { Textarea } from "@/components/ui/textarea";
import SermonComposer from "@/components/SermonComposer";
import { ListenIconButton } from "@/components/ListenButton";
import HighlightableText from "@/components/HighlightableText";
import { stop as stopAudio } from "@/lib/audioReader";
import { useDeeperNav } from "@/context/deeperNav";
import { MessageCircle, X, Send, Loader2, ScrollText, Info } from "lucide-react";

// The backend replies with this phrase (streamed mid-answer or as a 402/502 detail)
// when the study companion is temporarily down (e.g. AI provider credits exhausted).
const looksPaused = (t) => /briefly unavailable/i.test(t || "");

// variant:
//  - "floating" (mobile): a FAB that opens a bottom panel; self-managed.
//  - "embedded" (desktop): renders just the conversation (thread + input) to fill
//    a container the PARENT controls; the companion greets on mount.
// In both, the companion OPENS the conversation itself with a warm greeting.
export default function DeeperChat({ sessionId, initialChat = [], initialHighlights = [], variant = "floating", general = false, deeperId = null }) {
  const embedded = variant === "embedded";
  // The general floating companion now runs on an auto-saved "deeper conversation" so it
  // shows up in History. `convId` is that conversation's id (or an explicit deeperId page).
  const [convId, setConvId] = useState(deeperId);
  const ready = !general || !!convId; // for general, wait until a conversation is resolved
  const base = convId
    ? `/deeper/conversations/${convId}`
    : general
      ? `/companion`
      : `/study/sessions/${sessionId}`;
  const openerPath = `${base}/chat/opener`;
  const chatUrl = `${API_BASE}${base}/chat`;
  const highlightSessionId = convId || sessionId; // the underlying `sessions` doc id (studies and saved deeper conversations share one collection)
  const deeperSharePath = base === "/companion" ? undefined : `${base}/share`;
  const [open, setOpen] = useState(embedded);
  const [messages, setMessages] = useState(initialChat.map((m) => ({ role: m.role, content: m.content })));
  const [highlights, setHighlights] = useState(initialHighlights);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [openerLoading, setOpenerLoading] = useState(false);
  const openerTried = useRef(false);
  const resolving = useRef(false); // guards StrictMode double-invoke of the resolve effect
  const scrollRef = useRef(null);
  const taRef = useRef(null);
  const [feat, setFeat] = useState(null);
  const [sermonOpen, setSermonOpen] = useState(false);
  const [aiPaused, setAiPaused] = useState(false);
  const { setOpener } = useDeeperNav();

  // Resolve (or create) the auto-saved conversation for the general companion. The first
  // one adopts the user's existing rolling companion thread so nothing already said is lost.
  useEffect(() => {
    if (!general || convId || resolving.current) return;
    resolving.current = true;
    (async () => {
      const stored = localStorage.getItem("ow_deeper_id");
      if (stored) {
        try {
          const { data } = await api.get(`/deeper/conversations/${stored}`);
          setConvId(stored);
          setMessages((data.chat || []).map((m) => ({ role: m.role, content: m.content })));
          setHighlights(data.highlights || []);
          return;
        } catch {
          localStorage.removeItem("ow_deeper_id");
        }
      }
      try {
        const { data } = await api.post(`/deeper/conversations?adopt=true`);
        localStorage.setItem("ow_deeper_id", data.id);
        setConvId(data.id);
        setMessages((data.chat || []).map((m) => ({ role: m.role, content: m.content })));
        setHighlights(data.highlights || []);
      } catch {
        resolving.current = false; // allow a retry on next mount if it failed
      }
    })();
  }, [general, convId]);

  // Start a fresh saved conversation (general/deeper), archiving the current one to History.
  const newConversation = async () => {
    try {
      const { data } = await api.post(`/deeper/conversations`);
      if (general) localStorage.setItem("ow_deeper_id", data.id);
      openerTried.current = false;
      setMessages([]);
      setHighlights([]);
      setConvId(data.id);
    } catch { /* noop */ }
  };

  // Mobile: expose "open me" to the footer's prominent Go Deeper tab.
  useEffect(() => {
    if (embedded) return;
    setOpener(() => setOpen(true));
    return () => setOpener(null);
  }, [embedded, setOpener]);

  useEffect(() => {
    api.get("/me/subscription")
      .then(({ data }) => setFeat(data.features?.study_to_sermon || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, openerLoading]);

  // Stop narration when the chat unmounts.
  useEffect(() => () => stopAudio(), []);

  // When the conversation is open/mounted and empty, the companion greets first.
  useEffect(() => {
    if (!open || !ready || openerTried.current || messages.length > 0) return;
    openerTried.current = true;
    (async () => {
      setOpenerLoading(true);
      try {
        const { data } = await api.post(openerPath);
        if (data.content) setMessages([{ role: "assistant", content: data.content }]);
        setAiPaused(false);
      } catch (e) {
        openerTried.current = false;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 502 || looksPaused(detail)) setAiPaused(true);
      }
      setOpenerLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ready, convId]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming || !ready) return;
    setInput("");
    if (taRef.current) taRef.current.style.height = "auto"; // reset the grown input
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    const doFetch = () =>
      fetch(chatUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
    const setLast = (content) =>
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content };
        return copy;
      });
    try {
      let res = await doFetch();
      if (res.status === 401) {
        await fetch(`${API_BASE}/auth/refresh`, { method: "POST", credentials: "include" });
        res = await doFetch();
      }
      if (!res.ok) {
        let msg = "Sorry — I hit a snag. Please try again in a moment.";
        if (res.status === 401) {
          msg = "Your session timed out. Please sign in again to keep going — your study is saved.";
        } else if (res.status === 402) {
          try {
            const j = await res.json();
            msg = j.detail || "You're out of study credits for this. Top up or upgrade in Settings to keep going deeper.";
          } catch {
            msg = "You're out of study credits for this. Top up or upgrade in Settings to keep going deeper.";
          }
        }
        setLast(msg);
        setAiPaused(looksPaused(msg) || res.status === 502);
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let lastChunkAt = Date.now();
      let gotContent = false;
      let full = "";
      // The backend now sends an explicit end-of-stream marker ("\u0004") the instant the
      // answer is complete, so we settle on THAT — not on a short silence. The watchdog is
      // only a long last-resort safety net for a genuinely dead connection; a model pausing
      // mid-reply must NEVER truncate the message.
      const watchdog = setInterval(() => {
        if (gotContent && Date.now() - lastChunkAt > 90000) {
          try { reader.cancel(); } catch (e) { /* ignore */ }
        }
      }, 2000);
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          let chunk = decoder.decode(value, { stream: true });
          const eos = chunk.indexOf("\u0004");
          const finished = eos !== -1;
          if (finished) chunk = chunk.slice(0, eos); // strip the marker + anything after
          if (chunk) {
            lastChunkAt = Date.now();
            gotContent = true;
            full += chunk;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
              return copy;
            });
          }
          if (finished) break;
        }
      } finally {
        clearInterval(watchdog);
        setAiPaused(looksPaused(full));
      }
    } catch (e) {
      setLast("Sorry — I lost the connection. Try again.");
    }
    setStreaming(false);
  };

  const Thread = (
    <>
      {(general || deeperId) && (
        <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-1">
          <span data-testid="deeper-autosave" className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
            <ScrollText className="w-3.5 h-3.5 text-[#00E5FF]" strokeWidth={1.75} /> Saved automatically to your journey
          </span>
          <button
            data-testid="deeper-new-conversation"
            onClick={newConversation}
            className="text-[11px] text-[#00E5FF] hover:text-[#00B8CC] font-medium transition-colors"
          >
            + New conversation
          </button>
        </div>
      )}
      {aiPaused && (
        <div
          data-testid="ai-paused-banner"
          className="flex items-start gap-2 mx-4 mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs leading-relaxed text-amber-300"
        >
          <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.75} />
          <span>
            The study companion is briefly paused. Your studies, notes and messages are all
            saved — ask again shortly and we'll pick up right where you left off.
          </span>
        </div>
      )}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && openerLoading && (
          <div className="mr-auto bg-[#141B29] text-slate-400 rounded-2xl px-4 py-3 text-sm inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Settling in…
          </div>
        )}
        {messages.length === 0 && !openerLoading && (
          <p className="text-sm text-slate-500 text-center px-6 py-8 leading-relaxed">
            Ask a question, wrestle with something, or just say “help me understand this.”
          </p>
        )}
        {messages.map((m, i) => {
          const isAssistant = m.role === "assistant";
          const isStreamingLast = streaming && i === messages.length - 1;
          const canListen = isAssistant && (m.content || "").trim() && !isStreamingLast;
          return (
            <div key={i} className={m.role === "user" ? "flex flex-col items-end" : "flex flex-col items-start"}>
              <div
                data-testid={`chat-msg-${m.role}`}
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#00E5FF] text-[#07090E]" : "bg-[#141B29] text-slate-300"
                }`}
              >
                {m.content
                  ? (
                    <HighlightableText
                      sessionId={highlightSessionId}
                      block={`deeper-${i}`}
                      text={m.content}
                      highlights={highlights}
                      onChange={setHighlights}
                      editable={!!highlightSessionId && !isStreamingLast}
                      sharePath={deeperSharePath}
                      cardKind={m.role === "user" ? "reflection" : "companion"}
                    />
                  )
                  : isStreamingLast
                    ? (
                      <span className="inline-flex items-center gap-1 py-1" data-testid="chat-typing" aria-label="Thinking">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#3A4763] animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#3A4763] animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#3A4763] animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    )
                    : ""}
              </div>
              {canListen && (
                <div className="mt-1 ml-1">
                  <ListenIconButton
                    id={`chat-${i}`}
                    getText={() => m.content}
                    testid={`listen-chat-${i}`}
                    title="Listen to this answer"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="border-t border-[#1E273D] p-3 flex items-end gap-2">
        <Textarea
          ref={taRef}
          data-testid="deeper-input"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            const el = e.target;
            el.style.height = "auto";
            el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
          }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type your message…"
          rows={1}
          className="flex-1 resize-none border-0 bg-[#141B29] rounded-xl text-sm min-h-[44px] max-h-[200px] overflow-y-auto focus-visible:ring-1 focus-visible:ring-[#00E5FF]/30"
        />
        <button
          data-testid="deeper-send"
          onClick={send}
          disabled={streaming || !input.trim()}
          aria-label="Send message"
          title="Send"
          className="bg-[#00E5FF] text-[#07090E] rounded-xl p-3 disabled:opacity-40 hover:bg-[#00B8CC] transition-colors shrink-0"
        >
          {streaming ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" strokeWidth={1.5} />}
        </button>
      </div>
      {feat?.available && messages.filter((m) => (m.content || "").trim()).length >= 2 && (
        <div className="px-3 pb-3 -mt-1">
          <button
            data-testid="deeper-to-sermon"
            onClick={() => setSermonOpen(true)}
            disabled={streaming}
            className="w-full flex items-center justify-center gap-1.5 rounded-full border border-[#00E5FF]/30 py-2 text-xs text-[#00E5FF] hover:bg-[#10222A] transition-colors disabled:opacity-40"
          >
            <ScrollText className="w-3.5 h-3.5" strokeWidth={1.5} /> Turn this into a sermon
          </button>
        </div>
      )}
      <SermonComposer
        open={sermonOpen}
        onOpenChange={setSermonOpen}
        endpoint="/sermon/from-chat"
        body={general ? {} : { session_id: sessionId }}
        title="Sermon from your conversation"
        description="Shape what you've explored together into a preachable outline."
        cost={feat?.cost}
      />
    </>
  );

  // Desktop: parent controls visibility/size; we just fill the container.
  if (embedded) {
    return <div className="flex flex-col h-full min-h-0" data-testid="deeper-embedded">{Thread}</div>;
  }

  // Mobile: opened from the footer's Go Deeper tab; renders a bottom panel.
  return (
    <>
      {open && (
        <div
          data-testid="deeper-panel"
          className="fixed bottom-24 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[420px] z-50 bg-[#0E131F] rounded-3xl shadow-[0_8px_40px_rgba(0,0,0,0.15)] border border-[#1E273D] overflow-hidden flex flex-col h-[62vh] max-h-[600px] animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E273D]">
            <div>
              <div className="text-sm font-medium text-slate-200">Going deeper</div>
              <div className="text-xs text-slate-500">A conversation, not a lecture</div>
            </div>
            <button data-testid="deeper-close" onClick={() => setOpen(false)} className="p-2 text-slate-500 hover:text-slate-300">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
          {Thread}
        </div>
      )}
    </>
  );
}
